'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import NiceModal from '@ebay/nice-modal-react';
import { BuildingIcon, StoreIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { ProductCategory } from '@workspace/database';
import { routes } from '@workspace/routes';

import { getProductsForGenerator } from '~/actions/products/get-products-for-generator';
import { getPublicProductsForGenerator } from '~/actions/products/get-public-products-for-generator';
import {
  ProductCard,
  SelectedProductCard
} from '~/components/generator/cards/product-cards';
import {
  ItemSelectionStep,
  type ItemSelectionStepProps
} from '~/components/generator/shared/item-selection-step';
import type { ItemSortOption } from '~/components/generator/shared/item-selection-step';
import { AddProductModal } from '~/components/organizations/slug/products/add-product-modal';
import { useActiveOrganization } from '~/hooks/use-active-organization';
import { productCategoryLabel } from '~/lib/labels';
import type { CompleteGeneratorSchema } from '~/schemas/generator/complete-generator-schema';
import {
  GetProductsSortBy,
  ProductSourceOption
} from '~/schemas/products/get-products-schema';
import type { ProductDto } from '~/types/dtos/product-dto';
import type { TagDto } from '~/types/dtos/tag-dto';

const productSortOptions: ItemSortOption[] = [
  { value: GetProductsSortBy.Name, label: 'Name' },
  { value: GetProductsSortBy.Category, label: 'Category' },
  { value: GetProductsSortBy.CreatedAt, label: 'Created Date' },
  { value: GetProductsSortBy.UpdatedAt, label: 'Updated Date' }
];

const productSourceOptions = [
  {
    label: 'Sample Products',
    value: ProductSourceOption.Sample,
    icon: <StoreIcon className="size-4 shrink-0" />
  },
  {
    label: 'Organization Products',
    value: ProductSourceOption.Organization,
    icon: <BuildingIcon className="size-4 shrink-0" />
  }
];

type GeneratorProductsStepProps = React.HtmlHTMLAttributes<HTMLDivElement> &
  ItemSelectionStepProps<ProductDto, { tags: TagDto[] }>;

type GeneratorProductsStepLocalProps = Omit<
  GeneratorProductsStepProps,
  | 'fetcher'
  | 'onSelectionChange'
  | 'selectedIds'
  | 'ItemCardComponent'
  | 'SelectedItemCardComponent'
  | 'getFilterDefs'
  | 'onAddItem'
  | 'title'
  | 'description'
  | 'maxSelection'
  | 'sortOptions'
> & { initialSource?: ProductSourceOption };

export function GeneratorProductsStep({
  initialSource,
  ...props
}: GeneratorProductsStepLocalProps): React.JSX.Element {
  const methods = useFormContext<CompleteGeneratorSchema>();
  const selectedProducts = methods.watch('productStep.products');
  const router = useRouter();

  // Store fetched products to build complete product objects
  const [fetchedProducts, setFetchedProducts] = React.useState<ProductDto[]>(
    []
  );

  // Update selected products when fetched products change
  React.useEffect(() => {
    const currentSelectedProducts =
      methods.getValues('productStep.products') || [];
    if (currentSelectedProducts.length > 0) {
      const updatedProducts = currentSelectedProducts.map((selectedProduct) => {
        // If this is a fallback product (name is "Loading..."), try to find the real data
        if (selectedProduct.name === 'Loading...') {
          const realProductData = fetchedProducts.find(
            (p) => p.id === selectedProduct.id
          );
          if (realProductData) {
            return realProductData;
          }
        }
        return selectedProduct;
      });

      // Only update if there are changes
      const hasChanges = updatedProducts.some(
        (product, index) =>
          product.id !== currentSelectedProducts[index]?.id ||
          product.name !== currentSelectedProducts[index]?.name
      );

      if (hasChanges) {
        methods.setValue('productStep.products', updatedProducts, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
      }
    }
  }, [fetchedProducts, methods]);
  // Try to read organization context, but allow running without it (e.g. embedded)
  let organizationName: string | null = null;
  let hasOrganizationContext = false;
  try {
    organizationName = useActiveOrganization().name;
    hasOrganizationContext = true;
  } catch {
    // No active org provider – operate in public/sample-only mode
    organizationName = null;
    hasOrganizationContext = false;
  }

  const [productSource, setProductSource] = React.useState<ProductSourceOption>(
    hasOrganizationContext
      ? (initialSource ?? ProductSourceOption.Sample)
      : ProductSourceOption.Sample
  );

  const fetcher = React.useCallback(
    async (args: any) => {
      const action =
        hasOrganizationContext &&
        productSource === ProductSourceOption.Organization
          ? getProductsForGenerator
          : getPublicProductsForGenerator;
      const result = await action({
        pageIndex: args.pageIndex,
        pageSize: args.pageSize,
        sortBy: args.sortBy,
        sortDirection: args.sortDirection,
        searchQuery: args.searchQuery,
        categories: args.filters.categories ?? [],
        tags: args.filters.tags ?? []
      });

      console.log('RESULT', result);

      if (result?.data && !result.serverError && !result.validationErrors) {
        const { products, filteredCount, totalCount, tags } = result.data;

        // Store fetched products for building complete product objects
        setFetchedProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newProducts = products.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newProducts];
        });

        return {
          items: products,
          filteredCount,
          totalCount,
          tags
        };
      }

      return { items: [], filteredCount: 0, totalCount: 0, tags: [] };
    },
    [productSource, hasOrganizationContext]
  );

  const onSelectionChange = React.useCallback(
    (selectedIds: string[]) => {
      const newProducts = selectedIds.map((id) => {
        // Find the product data from fetched products
        const productData = fetchedProducts.find((p) => p.id === id);
        if (productData) {
          // Store the complete product data
          return productData;
        }

        // Fallback: create a minimal product object if data not found
        // This prevents the error and allows the selection to work
        // The complete data will be fetched when needed
        console.warn(
          `Product data not immediately available for id: ${id}, using fallback`
        );
        return {
          id,
          name: 'Loading...',
          sku: '',
          category: 'ACCESSORY' as const,
          createdAt: new Date(),
          updatedAt: new Date(),
          tags: [],
          images: [],
          generatedImages: []
        } as ProductDto;
      });

      methods.setValue('productStep.products', newProducts, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    },
    [methods, fetchedProducts]
  );

  const handleProductSourceChange = React.useCallback(
    (value: string) => {
      const newSource = value as ProductSourceOption;
      // When no organization context, only allow Sample
      if (!hasOrganizationContext) {
        setProductSource(ProductSourceOption.Sample);
        return;
      }
      if (newSource !== productSource) {
        setProductSource(newSource);
        // Reset pagination when switching tabs
        // The ItemSelectionStep will handle this automatically when fetcher changes
      }
    },
    [productSource, hasOrganizationContext]
  );

  const getFilterDefs = React.useCallback(
    (fetcherResult: { tags: TagDto[] } | undefined) => [
      {
        name: 'categories',
        title: 'Category',
        options: Object.values(ProductCategory).map((category) => ({
          value: category,
          label: productCategoryLabel[category]
        }))
      },
      {
        name: 'tags',
        title: 'Tags',
        options:
          fetcherResult?.tags.map((tag) => ({
            value: tag.text,
            label: tag.text
          })) ?? []
      }
    ],
    []
  );

  const handleAddProduct = async (
    onCreated?: (product: ProductDto) => void
  ) => {
    if (!hasOrganizationContext) {
      const callbackUrl = routes.dashboard.Index;
      window.open(
        routes.dashboard.auth.SignIn + `?callbackUrl=${callbackUrl}`,
        '_blank'
      );
      return;
    }
    NiceModal.show(AddProductModal, {
      onProductCreated: (newProduct: ProductDto) => {
        const currentProducts = methods.getValues('productStep.products') || [];
        if (currentProducts.length < 4) {
          // Store the complete product data
          methods.setValue(
            'productStep.products',
            [...currentProducts, newProduct],
            {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            }
          );

          // Switch to organization tab if not already there
          if (productSource !== ProductSourceOption.Organization) {
            setProductSource(ProductSourceOption.Organization);
          }

          // Ensure new product is available in fetched products list for selection resolution
          setFetchedProducts((prev) =>
            prev.find((p) => p.id === newProduct.id)
              ? prev
              : [newProduct, ...prev]
          );

          // Notify the ItemSelectionStep to append locally
          onCreated?.(newProduct);
        }
      }
    });
  };

  return (
    <div className="space-y-4">
      <ItemSelectionStep
        fetcher={fetcher}
        onSelectionChange={onSelectionChange}
        selectedIds={selectedProducts?.map((p) => p.id) || []}
        ItemCardComponent={ProductCard}
        SelectedItemCardComponent={SelectedProductCard}
        getFilterDefs={getFilterDefs}
        sortOptions={productSortOptions}
        onAddItem={handleAddProduct}
        title="Select your products"
        description="Select up to 2 products to place on your model."
        searchPlaceholder="Search by name or SKU..."
        maxSelection={2}
        {...(hasOrganizationContext
          ? {
              groupFilter: {
                options: productSourceOptions.map((opt) => ({
                  value: opt.value,
                  label:
                    opt.value === ProductSourceOption.Organization
                      ? `${organizationName} Products`
                      : opt.label,
                  icon: opt.icon
                })),
                value: productSource,
                onValueChange: handleProductSourceChange,
                srLabel: 'Product source'
              }
            }
          : {})}
        {...props}
      />
    </div>
  );
}
